import React from "react";
import { Sun, Moon, Star, CheckCircle, WarningCircle, XCircle, Clock } from "@phosphor-icons/react";

const scoreTone = (s) => (s >= 85 ? "bg-emerald-500" : s >= 70 ? "bg-lime-500" : s >= 55 ? "bg-amber-500" : s >= 40 ? "bg-orange-500" : "bg-rose-500");
// Inline rgba colours so verdicts stay distinct in both light and dark themes.
const tint = (rgb) => ({ background: `rgba(${rgb},0.16)`, borderColor: `rgba(${rgb},0.55)`, color: `rgb(${rgb})` });
const fmtDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short", weekday: "short" });

function Reasons({ good = [], bad = [], prefix }) {
  return (
    <ul className="mt-3 space-y-1 text-[12px]">
      {good.map((g, i) => <li key={`g${i}`} className="flex gap-1.5 text-emerald-600" data-testid={`${prefix}-good-${i}`}><CheckCircle size={14} weight="fill" className="shrink-0 mt-0.5" />{g}</li>)}
      {bad.map((b, i) => <li key={`b${i}`} className="flex gap-1.5 text-orange-500" data-testid={`${prefix}-bad-${i}`}><WarningCircle size={14} weight="fill" className="shrink-0 mt-0.5" />{b}</li>)}
    </ul>
  );
}

export function WindowCard({ w, rank, prefix, showMother, onSlot }) {
  return (
    <article className="rounded-2xl bg-white border border-amber-200/70 p-5 shadow-sm" data-testid={`${prefix}-${rank}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">#{rank + 1} · {w.weekday}</div>
          <div className="font-serif-display text-2xl text-[#3F2D1B] mt-0.5">{fmtDate(w.date)}</div>
          <div className="flex items-center gap-1.5 text-sm text-[#3F2D1B]/80 mt-1"><Clock size={14} /> {w.from} – {w.to} <span className="text-[#3F2D1B]/55">· best</span>
            <button type="button" onClick={() => onSlot?.({ date: w.date, time: w.best_time })} className="px-2 py-0.5 rounded-full border border-amber-500 text-amber-600 font-semibold hover:bg-amber-500 hover:text-white transition-colors" title="View the D1 chart for this moment" data-testid={`${prefix}-${rank}-best-time`}>{w.best_time}</button>
          </div>
        </div>
        <div className={`${scoreTone(w.score)} text-white rounded-xl px-3 py-2 text-center`}>
          <div className="text-xl font-bold leading-none" data-testid={`${prefix}-${rank}-score`}>{w.score}</div>
          <div className="text-[9px] uppercase tracking-wider">score</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3 text-[11px]">
        <Chip rgb="217,119,6">{w.lagna} lagna {w.lagna_degree}°</Chip>
        <Chip rgb="217,119,6">Lord {w.lagna_lord} in {w.lagna_lord_house}H</Chip>
        <Chip rgb="14,165,233">{w.nakshatra} {w.pada}</Chip>
        <Chip rgb="139,92,246">{w.tithi}</Chip>
        {showMother && w.tara && <Chip rgb="236,72,153">Mother: {w.tara} tara · {w.mother_score}</Chip>}
      </div>
      <Reasons good={w.good} bad={w.bad} prefix={`${prefix}-${rank}`} />
      {showMother && (w.mother_good?.length || w.mother_bad?.length) ? (
        <div className="mt-2 pt-2 border-t border-amber-100">
          <div className="text-[10px] uppercase tracking-wider text-pink-700 font-semibold">For the mother</div>
          <Reasons good={w.mother_good} bad={w.mother_bad} prefix={`${prefix}-${rank}-m`} />
        </div>
      ) : null}
    </article>
  );
}

export function ExclusionsPanel({ baby, stepMinutes }) {
  const reasons = Object.entries(baby.blocked_reasons || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="rounded-2xl border p-4 sm:p-5" style={{ background: "rgba(244,63,94,0.08)", borderColor: "rgba(244,63,94,0.35)" }} data-testid="baby-exclusions">
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-rose-500">Never recommended — classical exclusions</div>
      {baby.eclipses?.map((e) => (
        <p key={e.eclipse_from} className="text-sm mt-2" data-testid={`baby-eclipse-${e.kind}`}>
          <XCircle size={14} weight="fill" className="inline text-rose-500 mr-1" />
          <b>{e.name}</b>: {e.eclipse_from.slice(5)} → {e.eclipse_to.slice(11)}, with Sutak from {e.sutak_from.slice(5)}
        </p>
      ))}
      {baby.sankrantis?.map((s) => (
        <p key={s.date} className="text-sm mt-1" data-testid={`baby-sankranti-${s.date}`}>
          <XCircle size={14} weight="fill" className="inline text-rose-500 mr-1" /><b>Sankranti</b> on {fmtDate(s.date)}: the Sun enters {s.sign} (whole day excluded)
        </p>
      ))}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {reasons.map(([k, n]) => (
          <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border" style={tint("244,63,94")} data-testid={`baby-excl-${k.split(" ")[0]}`}>
            {k} · {Math.round((n * stepMinutes) / 60)}h
          </span>
        ))}
      </div>
    </div>
  );
}

export function DayStrip({ days }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-2" data-testid="baby-day-strip">
      {days.map((d) => (
        <div key={d.date} className="shrink-0 w-[68px] rounded-xl bg-white border border-amber-200/70 p-2 text-center" title={`${d.nakshatra} · ${d.tithi}`} data-testid={`baby-day-${d.date}`}>
          <div className="text-[10px] text-[#3F2D1B]/60">{d.weekday.slice(0, 3)}</div>
          <div className="text-sm font-semibold text-[#3F2D1B]">{d.date.slice(8)}/{d.date.slice(5, 7)}</div>
          <div className={`mt-1 h-1.5 rounded-full ${scoreTone(d.best_score)}`} style={{ opacity: d.best_score ? 1 : 0.25 }} />
          <div className="text-[11px] mt-1 font-bold text-[#3F2D1B]">{d.best_score || "—"}</div>
          <div className="text-[9px] text-[#3F2D1B]/55">{d.best_time || "blocked"}</div>
          {d.day_flags?.length > 0 && <div className="text-[8px] mt-0.5 text-rose-500 font-semibold leading-tight" data-testid={`baby-day-flag-${d.date}`}>{d.day_flags.map((f) => f.replace("Sutak before ", "Sutak · ").split(" — ")[0]).join(" · ")}</div>}
        </div>
      ))}
    </div>
  );
}

const VERDICT = {
  excellent: { style: tint("16,185,129"), Icon: CheckCircle },
  good: { style: tint("132,204,22"), Icon: CheckCircle },
  fair: { style: tint("245,158,11"), Icon: WarningCircle },
  caution: { style: tint("249,115,22"), Icon: WarningCircle },
  avoid: { style: tint("244,63,94"), Icon: XCircle },
};
const Chip = ({ rgb, children }) => <span className="px-2 py-0.5 rounded-full border" style={tint(rgb)}>{children}</span>;

export function MotherSection({ m }) {
  return (
    <section className="space-y-4" data-testid="mother-section">
      <SectionTitle icon={Moon} title="Mother's analysis" sub={`Natal Moon ${m.natal_moon_sign} · ${m.natal_nakshatra} nakshatra · Chandrashtama when the Moon transits ${m.chandrashtama_sign}`} />
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {m.days.map((d) => {
          const v = VERDICT[d.verdict];
          return (
            <div key={d.date} className="rounded-xl border p-3" style={v.style} data-testid={`mother-day-${d.date}`}>
              <div className="flex items-center justify-between text-[11px]"><span>{fmtDate(d.date)}</span><v.Icon size={14} weight="fill" /></div>
              <div className="text-sm font-semibold capitalize mt-1" data-testid={`mother-day-verdict-${d.date}`}>{d.verdict}</div>
              {d.chandrashtama.length > 0 && <div className="text-[10px] mt-1">Chandrashtama {d.chandrashtama.map((c) => `${c.from}–${c.to}`).join(", ")}</div>}
              <div className="text-[10px] mt-1 opacity-80">Tara: {d.taras.join(" → ")}</div>
              <div className="text-[10px] mt-0.5 opacity-80" data-testid={`mother-day-moon-${d.date}`}>
                Moon: {d.moon_signs?.map((m, i) => (
                  <span key={m.sign + m.from}>{i > 0 && ` → (${m.from}) `}<span className={m.house_from_mother === 8 ? "font-bold underline" : ""}>{m.sign}</span></span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function SectionTitle({ icon: Icon = Star, title, sub }) {
  return (
    <div>
      <h2 className="font-serif-display text-2xl sm:text-3xl text-[#3F2D1B] flex items-center gap-2"><Icon size={22} weight="duotone" className="text-amber-600" /> {title}</h2>
      {sub && <p className="text-xs sm:text-sm text-[#3F2D1B]/65 mt-1">{sub}</p>}
    </div>
  );
}

export { Sun };
